import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import uniqueId from 'lodash/uniqueId';
import c from 'classnames';
import type { PapiDeviation } from '@wix/da-papi-types';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import type { UploadAttachmentSuccessCallback } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { removeAttachment } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import FileUploadField from '@wix/da-shared-react/pkg/FileUploadField';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import {
  MIME_CONFIG,
  mimeTypeToPcpType,
} from '@wix/da-shared-react/pkg/utils/mime';
import { getSourceFilesInputName } from '../../redux/helpers';
import {
  getFileExtension,
  shouldRejectFile,
} from '../../../../fields/AttachmentsField/utils';
import s from './SourceFilesInput.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
  withLabel?: boolean;
  defaultValue?: any;
}

const SourceFilesInput: React.FC<Props> = ({
  deviation,
  className,
  withLabel = true,
  defaultValue,
}) => {
  const { t } = useTranslation();
  const { current: id } = useRef(uniqueId('source_files'));
  const { control, errors, setValue } = useFormContext();
  const dispatch = useDispatch();
  const fieldName = getSourceFilesInputName(deviation);
  const errorMessage = errors[fieldName]?.message;
  const [removeConfirmationFileId, setRemoveConfirmationFileId] =
    useState<number>(0);

  const {
    field: { ref, value: items, ...inputProps },
  } = useController({
    name: fieldName,
    control,
    defaultValue,
  });

  const handleUploadSuccess: UploadAttachmentSuccessCallback = useCallback(
    ({ deviationId, fileId, file: { name: fileName, size, type } }) => {
      const newItems = [
        ...items,
        {
          deviationId,
          fileId,
          name: fileName,
          size,
          type: mimeTypeToPcpType(type),
        },
      ];
      yieldToMain(() => {
        setValue(fieldName, newItems);
      });
    },
    [fieldName, items, setValue]
  );

  const handleUploadFailure = useCallback(
    (_, error) => {
      dispatch(
        addErrorNotification(
          t('deviationSubmit.validation.error.attachments.server', {
            error: error.message,
          })
        )
      );
    },
    [dispatch, t]
  );

  const onDrop = useCallback(
    (files, e): boolean => {
      const rejectedFile = files.find(shouldRejectFile);
      if (rejectedFile) {
        dispatch(
          addErrorNotification(
            t('deviationSubmit.validation.error.attachments.unsupported', {
              extension: getFileExtension(rejectedFile),
            })
          )
        );
        return false;
      } else {
        return true;
      }
    },
    [dispatch, t]
  );

  const handleRemoveAttachment = useCallback(
    fileId => {
      const newItems = items.map(item =>
        item.fileId === fileId ? { ...item, status: 'busy' } : item
      );
      yieldToMain(() => {
        setValue(fieldName, newItems);
      });
      dispatch(
        removeAttachment(fileId, deviation.deviationId, 'exclusive', () => {
          const newItems = items.filter(item => item.fileId !== fileId);
          yieldToMain(() => {
            setValue(fieldName, newItems);
          });
        })
      );
    },
    [fieldName, items, setValue, dispatch, deviation]
  );

  const handleRemove = useCallback(
    ({ deviationId, fileId }) => {
      if (deviationId === 0) {
        setRemoveConfirmationFileId(fileId);
      } else {
        handleRemoveAttachment(fileId);
      }
    },
    [setRemoveConfirmationFileId, handleRemoveAttachment]
  );

  const handleRemoveSource = useCallback(() => {
    const newItems = items.filter(
      item => item.fileId !== removeConfirmationFileId
    );
    setRemoveConfirmationFileId(0);
    yieldToMain(() => {
      setValue(fieldName, newItems);
    });
  }, [
    items,
    setValue,
    fieldName,
    removeConfirmationFileId,
    setRemoveConfirmationFileId,
  ]);

  const handleRemoveSourceCancel = useCallback(() => {
    setRemoveConfirmationFileId(0);
  }, [setRemoveConfirmationFileId]);

  return (
    <div className={c(s['root'], className)}>
      <FileUploadField
        id={getSourceFilesInputName(deviation)}
        uploadType={UploadType.ATTACHMENT}
        uploadParams={{
          parentDeviationId: deviation?.deviationId,
          attachmentType: 'exclusive',
        }}
        items={items}
        label={
          withLabel ? t('sellerModal.create.source_files.label') : undefined
        }
        subLabel={
          withLabel ? t('sellerModal.create.source_files.sublabel') : undefined
        }
        acceptedMimeTypes={MIME_CONFIG.forDeviationAttachment}
        onSuccess={handleUploadSuccess}
        onFailure={handleUploadFailure}
        onRemove={handleRemove}
        onDrop={onDrop}
        allowUpload
        uploadButtonInList
      />
      <input type="hidden" id={id} ref={ref} {...inputProps} />
      {errorMessage && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
      {removeConfirmationFileId > 0 && (
        <ConfirmationModal
          isOpen
          title={t('deviationSubmit.form.attachments.removeSource.title')}
          text={t('deviationSubmit.form.attachments.removeSource.text')}
          confirmBtnType="danger"
          confirmBtnLabel={t(
            'deviationSubmit.form.attachments.removeSource.confirmCta'
          )}
          cancelBtnLabel={t(
            'deviationSubmit.form.attachments.removeSource.cancelCta'
          )}
          onCancel={handleRemoveSourceCancel}
          onConfirm={handleRemoveSource}
        />
      )}
    </div>
  );
};
SourceFilesInput.displayName = 'SourceFilesInput';

export default SourceFilesInput;
